<script setup lang="ts">
  import { CallsService, OrganisationsService, type SuperUserCall as Call } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type Filter,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import CustomerTableDataCell from '@/components/admin/table/CustomerTableDataCell.vue'
  import CustomerDisplay from '@/components/admin/CustomerDisplay.vue'
  import { ROUTES } from '@/router/routes'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import { formatDateTimeGerman } from '../../../lib/util/formatting'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { Badge } from '@/components/ui/badge'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { PencilSquareIcon } from '@heroicons/vue/24/outline'
  import geminiIcon from '@/assets/gemini-icon.svg'
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

  const customerOptions = ref<{ label: string; value: string }[]>([])
  const tableContainerRef = ref<HTMLElement | null>(null)
  const isCompact = ref(false)
  let resizeObserver: ResizeObserver | null = null

  // Watch for the ref to be set, then attach ResizeObserver
  watch(
    tableContainerRef,
    (el) => {
      resizeObserver?.disconnect()
      if (el) {
        resizeObserver = new ResizeObserver((entries) => {
          const width = entries[0]?.contentRect.width ?? 0
          isCompact.value = width < 1000
        })
        resizeObserver.observe(el)
      }
    },
    { immediate: true }
  )

  onMounted(async () => {
    const response = await OrganisationsService.organisationsList({ limit: 250 })
    customerOptions.value = (response.results ?? []).map((org: any) => ({
      label: org.name,
      value: org.uuid
    }))
  })

  onUnmounted(() => {
    resizeObserver?.disconnect()
  })

  const classificationLabel = (value: string | undefined): string => {
    if (!value) return '-'
    const labels: Record<string, string> = {
      NEW_CLIENT_CALL: 'New Client',
      EXISTING_CLIENT_CALL_BACK: 'Existing',
      QUESTION: 'Question',
      GENERAL_ENQUIRY: 'Enquiry',
      NOT_COMPLETED: 'Not Completed',
      WITHOUT_MESSAGE: 'No Message',
      LAWFIRM_PARTNER: 'Partner',
      COURT: 'Court',
      SPAM: 'Spam',
      CALL_FORWARDED: 'Forwarded'
    }
    return labels[value] || value
  }

  const fetchCalls = async (options?: FetchDataOptions): Promise<PaginatedResponse<Call>> => {
    const filters = options?.filters || []
    const durationRangeFilter = filters.find((filter) => filter.key === 'duration')?.value
    const humanRatingFilter = filters.find((filter) => filter.key === 'humanRating')?.value
    const aiRatingFilter = filters.find((filter) => filter.key === 'rating')?.value
    const callFlagsVal = filters.find((filter) => filter.key === 'callFlags')?.value
    const callFlags = callFlagsVal ? new Set(callFlagsVal.split(',')) : new Set<string>()
    const forwardedOnlyFilter = callFlags.has('forwarded') ? true : undefined
    const geminiShadowOnlyFilter = callFlags.has('gemini') ? true : undefined
    const editedOnlyFilter = callFlags.has('edited') ? true : undefined
    const classificationFilter = filters.find(
      (filter) => filter.key === 'resultClassification'
    )?.value
    const orgFilter = filters.find((filter) => filter.key === 'org')?.value
    const dateRangeValue = filters.find((filter) => filter.key === 'dateRange')?.value
    let startDate: string | undefined
    let endDate: string | undefined
    if (dateRangeValue) {
      const parts = dateRangeValue.split(',')
      if (parts.length === 2) {
        startDate = parts[0]?.trim() || undefined
        endDate = parts[1]?.trim() || undefined
      }
    }

    return CallsService.callsList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      durationRanges: durationRangeFilter,
      humanRatings: humanRatingFilter,
      aiRatings: aiRatingFilter,
      forwardedOnly: forwardedOnlyFilter,
      geminiShadowOnly: geminiShadowOnlyFilter,
      editedOnly: editedOnlyFilter,
      resultClassification: classificationFilter,
      org: orgFilter,
      startDate,
      endDate
    }) as unknown as PaginatedResponse<Call>
  }

  const filterableFields = computed<Filter[]>(() => [
    {
      key: 'org',
      label: 'Customer',
      type: 'select',
      options: customerOptions.value
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'date-range'
    },
    {
      key: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { label: 'Less than 20s', value: '0-20' },
        { label: '20 - 59 seconds', value: '20-59' },
        { label: '1 - 3 minutes', value: '60-180' },
        { label: 'More than 3 minutes', value: '180-660' }
      ]
    },
    {
      key: 'humanRating',
      label: 'Rating by Human',
      type: 'multi-select',
      options: [
        { label: 'No rating', value: 'None' },
        { label: '1 rating', value: '1' },
        { label: '2 rating', value: '2' },
        { label: '3 rating', value: '3' },
        { label: '4 rating', value: '4' },
        { label: '5 rating', value: '5' }
      ]
    },
    {
      key: 'rating',
      label: 'Rating by AI',
      type: 'multi-select',
      options: [
        { label: 'No rating', value: 'None' },
        { label: '1 rating', value: '1' },
        { label: '2 rating', value: '2' },
        { label: '3 rating', value: '3' },
        { label: '4 rating', value: '4' },
        { label: '5 rating', value: '5' }
      ]
    },
    {
      key: 'callFlags',
      label: 'Filters',
      type: 'multi-select',
      options: [
        { label: 'Forwarded', value: 'forwarded' },
        { label: 'Gemini Shadow', value: 'gemini' },
        { label: 'Edited', value: 'edited' }
      ]
    },
    {
      key: 'resultClassification',
      label: 'Classification',
      type: 'select',
      options: [
        { label: 'New Client', value: 'NEW_CLIENT_CALL' },
        { label: 'Existing Client', value: 'EXISTING_CLIENT_CALL_BACK' },
        { label: 'Question', value: 'QUESTION' },
        { label: 'General Enquiry', value: 'GENERAL_ENQUIRY' },
        { label: 'Not Completed', value: 'NOT_COMPLETED' },
        { label: 'Without Message', value: 'WITHOUT_MESSAGE' },
        { label: 'Law Firm Partner', value: 'LAWFIRM_PARTNER' },
        { label: 'Court', value: 'COURT' },
        { label: 'Spam', value: 'SPAM' },
        { label: 'Forwarded', value: 'CALL_FORWARDED' }
      ]
    }
  ])
</script>
<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Calls', to: ROUTES.ADMIN.VOICE.CALL_LIST }
          ]"
        />
      </div>
      <!-- End Page Header -->

      <!-- Users Table Card -->
      <div
        ref="tableContainerRef"
        class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
      >
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="w-full p-0.5 align-middle">
                <!-- Table -->
                <PaginatedTableView
                  :fetchData="fetchCalls"
                  searchableFields="UUID, phone numbers or customer name"
                  :filterableFields="filterableFields"
                  @update-filters="($event: any) => console.log($event)"
                >
                  <template #table-head>
                    <TableCell>UUID</TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">Datetime</TableCell>
                    <TableCell
                      v-if="isCompact"
                      class="w-[1%] whitespace-nowrap px-5"
                    >
                      Phone
                    </TableCell>
                    <template v-else>
                      <TableCell class="w-[1%] whitespace-nowrap px-5">From</TableCell>
                      <TableCell class="w-[1%] whitespace-nowrap px-5">To</TableCell>
                    </template>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">Classification</TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">Customer</TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">Duration</TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">Rating</TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">Actions</TableCell>
                  </template>
                  <template #table-row="{ items: calls }: { items: Call[] }">
                    <TableRow
                      v-for="call in calls"
                      :key="call.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="ROUTES.ADMIN.VOICE.CALL_DETAIL.replace(':uuid', (call as Call).uuid)"
                        >
                          {{ call.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell class="whitespace-nowrap text-center">
                        <div class="inline-flex items-center gap-x-1.5">
                          <span>
                            {{
                              call.call_start_time
                                ? formatDateTimeGerman(call.call_start_time)
                                : '-'
                            }}
                          </span>
                          <TooltipProvider v-if="call.has_contact_corrections">
                            <Tooltip>
                              <TooltipTrigger>
                                <PencilSquareIcon class="h-4 w-4 shrink-0 text-purple-500" />
                              </TooltipTrigger>
                              <TooltipContent>Edited</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <!-- Stacked phone column (compact) -->
                      <TableCell
                        v-if="isCompact"
                        class="max-w-[140px] text-center text-sm"
                      >
                        <div
                          class="truncate"
                          :title="call.user_phone_number"
                        >
                          {{ call.user_phone_number }}
                        </div>
                        <div
                          class="truncate text-xs text-gray-400"
                          :title="call.phone_number.phone_number"
                        >
                          → {{ call.phone_number.phone_number }}
                        </div>
                      </TableCell>
                      <!-- Separate phone columns (wide) -->
                      <template v-else>
                        <TableCell
                          class="whitespace-nowrap text-center text-sm"
                          :title="call.user_phone_number"
                        >
                          {{ call.user_phone_number }}
                        </TableCell>
                        <TableCell
                          class="whitespace-nowrap text-center text-sm"
                          :title="call.phone_number.phone_number"
                        >
                          {{ call.phone_number.phone_number }}
                        </TableCell>
                      </template>
                      <TableCell class="whitespace-nowrap text-center">
                        <Badge
                          variant="secondary"
                          class="text-xs"
                        >
                          {{ classificationLabel(call.result?.classification) }}
                        </Badge>
                      </TableCell>
                      <TableCell
                        v-if="isCompact"
                        class="whitespace-nowrap px-4 py-1"
                      >
                        <CustomerDisplay
                          :org="call.phone_number.org"
                          :small="true"
                        />
                      </TableCell>
                      <CustomerTableDataCell
                        v-else
                        :org="call.phone_number.org"
                      />
                      <TableCell class="text-center">
                        {{ call.duration ? call.duration + 's' : '-' }}
                      </TableCell>
                      <TableCell class="whitespace-nowrap text-center">
                        <div class="inline-flex items-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge
                                  class="cursor-pointer text-xs"
                                  variant="secondary"
                                >
                                  {{ call.system_rating ? call.system_rating : '-' }}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>AI</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge
                                  class="cursor-pointer text-xs"
                                  variant="default"
                                >
                                  {{
                                    call.system_rating_by_human ? call.system_rating_by_human : '-'
                                  }}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>Human</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell class="whitespace-nowrap px-2 text-center">
                        <router-link
                          class="text-sm font-semibold text-blue-600 hover:text-blue-800"
                          :to="ROUTES.ADMIN.VOICE.CALL_DETAIL.replace(':uuid', call.uuid)"
                        >
                          Details
                        </router-link>
                        <img
                          v-if="call.gemini_shadow_comparison"
                          :src="geminiIcon"
                          class="ml-1 inline h-4 w-4"
                          alt="Gemini"
                        />
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
